import client from '../api/client.ts';
import { ep } from '../api/endpoints.ts';
import type { LogHistoryPage, LogQuery } from '../types/logs.ts';
import type { SuperOverview } from '../types/overview.ts';
import { buildDemoMatrixApplyPath } from './demoMatrixRoute.ts';
import { normalizeLogHistoryPage } from './logs.ts';

const api = client;
const toPublicApiUrl = (url: string): string =>
  url.startsWith('/api/')
    ? url
    : `/api/v1${url.startsWith('/') ? url : `/${url}`}`;

// Extended API service with additional methods
const apiService = {
  ...api,
  
  // Add support for FormData uploads
  postFormData: (url: string, formData: FormData) => {
    return api.post(url, formData, {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
    });
  },
  
  // Add public endpoint support (no auth)
  getPublic: (url: string) => api.get(toPublicApiUrl(url)),
};

const toNumber = (value: unknown): number => {
  const parsed = Number(value);
  return Number.isFinite(parsed) ? parsed : 0;
};

const toStringOrNull = (value: unknown): string | null =>
  typeof value === 'string' && value.length > 0 ? value : null;

const toStringArray = (value: unknown): string[] =>
  Array.isArray(value) ? value.map((entry) => String(entry)).filter(Boolean) : [];

const normalizeOverview = (raw: any): SuperOverview => ({
  health: {
    status: typeof raw?.health?.status === 'string' ? raw.health.status : 'degraded',
    logsLastHour: toNumber(raw?.health?.logsLastHour),
    errorsLastHour: toNumber(raw?.health?.errorsLastHour),
    warningsLastHour: toNumber(raw?.health?.warningsLastHour),
  },
  tenants: {
    total: toNumber(raw?.tenants?.total),
    active: toNumber(raw?.tenants?.active),
    pendingInvites: toNumber(raw?.tenants?.pendingInvites),
  },
  systemTotals: {
    locations: toNumber(raw?.systemTotals?.locations),
    spaces: toNumber(raw?.systemTotals?.spaces),
    tables: toNumber(raw?.systemTotals?.tables),
    users: toNumber(raw?.systemTotals?.users),
    customers: toNumber(raw?.systemTotals?.customers),
    reservations: toNumber(raw?.systemTotals?.reservations),
    tableReservations: toNumber(raw?.systemTotals?.tableReservations),
    reservationsPending: toNumber(raw?.systemTotals?.reservationsPending),
    reservationsActive: toNumber(raw?.systemTotals?.reservationsActive),
  },
  logSummary: {
    recentActivity: toNumber(raw?.logSummary?.recentActivity),
    recentErrors: toNumber(raw?.logSummary?.recentErrors),
    recentWarnings: toNumber(raw?.logSummary?.recentWarnings),
  },
  pendingInvites: Array.isArray(raw?.pendingInvites)
    ? raw.pendingInvites.map((invite: any) => ({
        id: toNumber(invite?.id),
        email: typeof invite?.email === 'string' ? invite.email : '',
        tenantId: invite?.tenantId == null ? null : toNumber(invite.tenantId),
        tenantName: toStringOrNull(invite?.tenantName),
        createdAt: toStringOrNull(invite?.createdAt),
        expiresAt: toStringOrNull(invite?.expiresAt),
      }))
    : [],
  recentTenants: Array.isArray(raw?.recentTenants)
    ? raw.recentTenants.map((tenant: any) => ({
        id: toNumber(tenant?.id),
        name: typeof tenant?.name === 'string' ? tenant.name : 'Unknown tenant',
        subdomain: toStringOrNull(tenant?.subdomain),
        city: toStringOrNull(tenant?.city),
        status: toStringOrNull(tenant?.status),
        createdAt: toStringOrNull(tenant?.createdAt),
        enabledModules: toStringArray(tenant?.enabledModules),
        logoUrl: toStringOrNull(tenant?.logoUrl),
      }))
    : [],
  recentLogs: Array.isArray(raw?.recentLogs)
    ? raw.recentLogs.map((entry: any) => ({
        id: toNumber(entry?.id),
        level: typeof entry?.level === 'string' ? entry.level : 'INFO',
        message: typeof entry?.message === 'string' ? entry.message : '',
        component: toStringOrNull(entry?.component),
        source: toStringOrNull(entry?.source),
        timestamp: toStringOrNull(entry?.timestamp) || '',
      }))
    : [],
});

export interface SupportAssignment {
  id: number;
  userId: number | null;
  userName: string | null;
  userEmail: string | null;
  tenantId: number | null;
  tenantName: string | null;
  locationId: number | null;
  locationName: string | null;
  assignmentRoleCode: string | null;
  permissionProfileCode: string | null;
  primary: boolean;
  status: string | null;
  startAt: string | null;
  endAt: string | null;
  notes: string | null;
  allowOverrides: string[];
  denyOverrides: string[];
}

export interface SupportCatalog {
  menus: Array<{
    menuKey: string;
    appScope: string;
    moduleCode?: string | null;
    label: string;
    route?: string | null;
  }>;
  profiles: Array<{
    code: string;
    appScope: string;
    name: string;
    description?: string | null;
    grants: string[];
  }>;
}

export interface SupportActionLog {
  id: number;
  actorUserId: number | null;
  tenantId: number | null;
  locationId: number | null;
  targetEntity: string;
  targetId: string;
  actionCode: string;
  reason: string;
  result?: string | null;
  payloadJson?: string | null;
  createdAt?: string | null;
}

export interface VerticalPackageModule {
  moduleCode: string;
  moduleName: string;
  category?: string | null;
  verticalCode?: string | null;
  required?: boolean;
  enabledByDefault?: boolean;
  active?: boolean;
}

export interface VerticalPackage {
  packageCode: string;
  packageName: string;
  verticalCode?: string | null;
  description?: string | null;
  active: boolean;
  modules: VerticalPackageModule[];
}

export interface TenantPackageEntitlement {
  entitlementId: number;
  packageCode: string;
  packageName: string;
  verticalCode?: string | null;
  status: string;
  startsAt?: string | null;
  endsAt?: string | null;
  source?: string | null;
  active: boolean;
  modules: VerticalPackageModule[];
  createdAt?: string | null;
  updatedAt?: string | null;
}

export interface GrantTenantPackagePayload {
  packageCode: string;
  status?: string;
  startsAt?: string | null;
  endsAt?: string | null;
  source?: string | null;
}

export interface VisibilityWarning {
  code: string;
  message: string;
  severity?: string | null;
}

export interface TenantVisibilityPreview {
  tenantId: number;
  userId?: number | null;
  visibilityContext?: {
    defaultScope?: { scopeKey: string; label?: string | null } | null;
    availableScopes?: Array<{ scopeKey: string; label?: string | null; scopeType?: string | null }>;
    visibleModules?: Array<{ code: string; name: string; category?: string | null; verticalCode?: string | null }>;
    warnings?: VisibilityWarning[];
  } | null;
  packageEntitlements: TenantPackageEntitlement[];
  venueCapabilities: Array<{
    venueId?: number | null;
    venueCode?: string | null;
    venueName?: string | null;
    verticalCode?: string | null;
    enabled?: boolean;
    primaryForVenue?: boolean;
  }>;
}

export interface VerticalDemoTemplate {
  code: string;
  label: string;
  description?: string | null;
  packages: string[];
  venues: Array<unknown>;
  expectedScopes: string[];
  expectedModules: string[];
  demoOnly: boolean;
}

export interface ApplyVerticalDemoTemplatePayload {
  primaryUserId?: number | null;
  dryRun?: boolean;
}

export interface VerticalTemplateApplyResult {
  dryRun: boolean;
  plannedChanges?: string[];
  appliedChanges?: string[];
  warnings?: string[];
}

export interface DemoMatrixProfile {
  code: string;
  label: string;
  description?: string | null;
  verticals: string[];
  packages: string[];
  expectedRoutes: string[];
  forbiddenRoutes: string[];
  expectedScopes: string[];
  dataDepth: string;
}

export interface DemoMatrixWarning {
  code: string;
  message: string;
  severity?: string | null;
}

export interface DemoMatrixVerticalSeedSummary {
  code: string;
  label: string;
  verticalCode: string;
  dataDepth: string;
  dryRun: boolean;
  plannedChanges: string[];
  appliedChanges: string[];
  warnings: DemoMatrixWarning[];
}

export interface DemoMatrixApplyPayload {
  dryRun?: boolean;
  resetExisting?: boolean;
  applyFitnessFullData?: boolean;
  applyVerticalBusinessData?: boolean;
}

export interface DemoMatrixApplyResult {
  profileCode: string;
  dryRun: boolean;
  plannedChanges: string[];
  appliedChanges: string[];
  vpfSummary: VerticalTemplateApplyResult | null;
  verticalSeedSummaries: DemoMatrixVerticalSeedSummary[];
  warnings: DemoMatrixWarning[];
}

export const setTenantModules = (tenantId:number, enabledModules:string[]) =>
  api.put(`/api/v1/admin/tenants/${tenantId}/enabled-modules`, { enabledModules }).then(r=>r.data);

/* ---------------- Users ---------------- */
export const getUsers = (page=0, query='') =>
  api.get(ep.users,{params:{page,query}}).then(r=>r.data);

// Get users with HR profiles with pagination and filters
export const getUsersWithHr = (params?: {
  search?: string;
  status?: string;
  jobTitle?: string;
  page?: number;
  size?: number;
  sort?: string;
}) => {
  // Build query params
  const queryParams: any = {};
  if (params?.search) queryParams.search = params.search;
  if (params?.status) queryParams.status = params.status;
  if (params?.jobTitle) queryParams.jobTitle = params.jobTitle;
  if (params?.page !== undefined) queryParams.page = params.page;
  if (params?.size !== undefined) queryParams.size = params.size;
  if (params?.sort) queryParams.sort = params.sort;
  
  // For backward compatibility, check if we want simple list
  const isSimpleList = !params || (params.page === undefined && params.size === undefined);
  if (isSimpleList) {
    queryParams.simple = true;
  }

  return api.get('/api/v1/users-with-hr', { params: queryParams }).then(r => {
    // Handle paginated response
    if (r.data.content) {
      return {
        content: r.data.content.map((user: any) => ({
          userId: user.user_id || user.userId,
          firstName: user.first_name || user.firstName,
          lastName: user.last_name || user.lastName,
          email: user.email,
          tenantId: user.tenant_id || user.tenantId,
          roles: user.roles,
          hrProfile: user.hr_profile ? {
            jobTitle: user.hr_profile.job_title || user.hr_profile.jobTitle,
            employmentStartDate: user.hr_profile.employment_start_date || user.hr_profile.employmentStartDate,
            status: user.hr_profile.status,
            emergencyContactName: user.hr_profile.emergency_contact_name || user.hr_profile.emergencyContactName,
            emergencyContactPhone: user.hr_profile.emergency_contact_phone || user.hr_profile.emergencyContactPhone,
            workSchedule: user.hr_profile.work_schedule || user.hr_profile.workSchedule,
            notes: user.hr_profile.notes,
          } : null
        })),
        totalElements: r.data.totalElements || r.data.total_elements,
        totalPages: r.data.totalPages || r.data.total_pages,
        number: r.data.number,
        size: r.data.size,
        first: r.data.first,
        last: r.data.last
      };
    }
    
    // Handle simple list response (backward compatibility)
    if (Array.isArray(r.data)) {
      return r.data.map((user: any) => ({
        userId: user.user_id || user.userId,
        firstName: user.first_name || user.firstName,
        lastName: user.last_name || user.lastName,
        email: user.email,
        tenantId: user.tenant_id || user.tenantId,
        roles: user.roles,
        hrProfile: user.hr_profile ? {
          jobTitle: user.hr_profile.job_title || user.hr_profile.jobTitle,
          employmentStartDate: user.hr_profile.employment_start_date || user.hr_profile.employmentStartDate,
          status: user.hr_profile.status,
          emergencyContactName: user.hr_profile.emergency_contact_name || user.hr_profile.emergencyContactName,
          emergencyContactPhone: user.hr_profile.emergency_contact_phone || user.hr_profile.emergencyContactPhone,
          workSchedule: user.hr_profile.work_schedule || user.hr_profile.workSchedule,
          notes: user.hr_profile.notes,
        } : null
      }));
    }
    
    return r.data;
  });
};

export const updateUserRoles = (userId:number, roles:string[]) =>
  api.put(`${ep.users}/${userId}/roles`, { roles }).then(r=>r.data);

// DELETE is exposed at non-super endpoint on backend
export const deleteUser = (userId:number) =>
  api.delete(`/api/v1/users/${userId}`).then(r=>r.data);

/* ---------------- Reservations ---------------- */
export const getReservations = (tenantId:string,from?:string,to?:string)=>
  api.get(ep.tenantTableReservations(tenantId),{params:{from,to}}).then(r=>r.data);
export const createGuestCustomer = (payload:any) =>
  api.post('/api/v1/customers/guest', payload).then(r => r.data);
export const createReservation = (tenantId:string,payload:any) =>
  api.post(ep.tenantTableReservations(tenantId), payload).then(r => r.data);
export const confirmReservation = (tenantId:string,id:number|string) =>
  api.put(`${ep.tenantTableReservations(tenantId)}/${id}/confirm`).then(r => r.data);
export const rejectReservation = (tenantId:string,id:number|string) =>
  api.put(`${ep.tenantTableReservations(tenantId)}/${id}/reject`).then(r => r.data);
export const cancelReservation = (tenantId:string,id:number|string) =>
  api.put(`${ep.tenantTableReservations(tenantId)}/${id}/cancel`).then(r => r.data);

/* tables */
export const getTables = (spaceId:string)=> api.get(ep.spaceTables(spaceId)).then(r=>r.data);
export const createTable = (spaceId:string,d:any)=> api.post(ep.spaceTables(spaceId),d).then(r=>r.data);
export const updateTable = (spaceId:string,id:string,d:any)=> api.put(`${ep.spaceTables(spaceId)}/${id}`,d).then(r=>r.data);
export const deleteTable = (spaceId:string,id:string)=> api.delete(`${ep.spaceTables(spaceId)}/${id}`).then(r=>r.data);

/* spaces */
export const getSpaces=(locationId:string)=>api.get(ep.locationSpaces(locationId)).then(r=>r.data);
export const createSpace=(locId:string,d:any)=>api.post(ep.locationSpaces(locId),d).then(r=>r.data);
export const updateSpace=(locId:string,id:string,d:any)=>api.put(`${ep.locationSpaces(locId)}/${id}`,d).then(r=>r.data);
export const deleteSpace=(locId:string,id:string)=>api.delete(`${ep.locationSpaces(locId)}/${id}`).then(r=>r.data);

/* locations */
export const getLocations=(tenantId:string)=>api.get(ep.tenantLocations(tenantId)).then(r=>r.data);
export const createLocation=(t:string,d:any)=>api.post(ep.tenantLocations(t),d).then(r=>r.data);
export const updateLocation=(t:string,id:string,d:any)=>api.put(`${ep.tenantLocations(t)}/${id}`,d).then(r=>r.data);
export const deleteLocation=(t:string,id:string)=>api.delete(`${ep.tenantLocations(t)}/${id}`).then(r=>r.data);

/* tenants */
export const getTenants = (page=0, query='') =>
  api.get(ep.tenants, { params:{page,query} }).then(r=>r.data);
export const createTenant = (payload:any)=> api.post(ep.tenants,payload).then(r=>r.data).catch((e)=>{ throw e; });
export const getTenant = (id:number|string)=>api.get(`${ep.tenants}/${id}`).then(r=>r.data);

export type TenantWelcomeStatus =
  | 'PENDING'
  | 'SENDING'
  | 'SENT'
  | 'FAILED'
  | 'UNDELIVERED'
  | 'ACTIVATED';

export interface TenantWelcomeDelivery {
  tenantId?: number | null;
  status?: TenantWelcomeStatus | null;
  ownerEmailMasked?: string | null;
  attempts?: number | null;
  createdAt?: string | null;
  lastAttemptAt?: string | null;
  sentAt?: string | null;
  activatedAt?: string | null;
  nextResendAt?: string | null;
  resendAllowed?: boolean | null;
  safeDetail?: string | null;
}

export const getTenantWelcome = (tenantId:number|string): Promise<TenantWelcomeDelivery> =>
  api.get(`/api/v1/super/tenants/${tenantId}/welcome`).then(r=>r.data);

export const resendTenantWelcome = (tenantId:number|string): Promise<TenantWelcomeDelivery> =>
  api.post(`/api/v1/super/tenants/${tenantId}/welcome/resend`).then(r=>r.data);

/* misc */
export const globalSearch=(entity:string,query:string,page=0)=>api.get(ep.search,{params:{entity,query,page}}).then(r=>r.data);
export const deleteTenant=(id:string)=>api.delete(`${ep.tenants}/${id}`);
export const updateTenant=(id:number,p:any)=>api.put(`${ep.tenants}/${id}`,p).then(r=>r.data);
export const suspendTenant=(id:number)=>api.delete(`${ep.tenants}/${id}`);
export const activateTenant=(id:number)=>api.put(`${ep.tenants}/${id}`,{status:'ACTIVE'}).then(r=>r.data);
export const getTenantStats = (id:number|string)=>api.get(`${ep.tenants}/${id}/stats`).then(r=>r.data);
export const createBillingCheckout = (payload:{tenantId:number|string; priceId:string}, idempotencyKey: string) =>
  api.post('/api/v1/billing/checkout', payload, { headers: { 'X-Idempotency-Key': idempotencyKey } }).then(r=>r.data);
export const reconcileTenantBilling = (tenantId:number|string) =>
  api.post(`/api/v1/super/tenants/${tenantId}/billing/reconcile`).then(r=>r.data);
export const getManagers = (tenantId:string)=> api.get(ep.managers(tenantId)).then(r=>r.data);
export const inviteManager = (tenantId:number, email:string, extra?:{firstName?:string;lastName?:string;country?:string;city?:string})=>
  api.post(ep.inviteManager(tenantId), { email, ...extra }).then(r=>r.data);

/* invitations */
export const acceptInvite = (payload:{token:string;firstName:string;lastName:string;password:string})=>
  api.post(ep.inviteAccept, payload).then(r=>r.data);

export const getOverview = (): Promise<SuperOverview> =>
  api.get('/api/v1/super/overview').then(r => normalizeOverview(r.data));

export const getSupportAccessCatalog = (): Promise<SupportCatalog> =>
  api.get(ep.supportAccessCatalog).then((r) => r.data);

export const getSupportAssignments = (params?: {
  tenantId?: number | string;
  userId?: number | string;
  locationId?: number | string;
}): Promise<SupportAssignment[]> =>
  api.get(ep.supportAssignments, { params }).then((r) => r.data);

export const getSupportActions = (params?: {
  tenantId?: number | string;
  locationId?: number | string;
  query?: string;
  page?: number;
  size?: number;
}): Promise<{ content: SupportActionLog[]; totalElements?: number }> =>
  api.get(ep.supportActions, { params }).then((r) => r.data);

export const createSupportAction = (payload: {
  tenantId?: number | string | null;
  locationId?: number | string | null;
  targetEntity: string;
  targetId: string;
  actionCode: string;
  reason: string;
  result?: string;
  payloadJson?: string;
}): Promise<SupportActionLog> =>
  api.post(ep.supportActions, payload).then((r) => r.data);

/* vertical packaging */
export const listVerticalPackages = (): Promise<VerticalPackage[]> =>
  api.get('/api/v1/super/vertical-packaging/packages').then((r) => r.data);

export const listTenantPackageEntitlements = (
  tenantId: number | string
): Promise<TenantPackageEntitlement[]> =>
  api.get(`/api/v1/super/tenants/${tenantId}/vertical-packaging/package-entitlements`).then((r) => r.data);

export const grantTenantPackage = (
  tenantId: number | string,
  payload: GrantTenantPackagePayload
): Promise<TenantPackageEntitlement> =>
  api.post(`/api/v1/super/tenants/${tenantId}/vertical-packaging/package-entitlements`, payload).then((r) => r.data);

export const expireTenantPackage = (
  tenantId: number | string,
  entitlementId: number | string
): Promise<TenantPackageEntitlement> =>
  api.post(`/api/v1/super/tenants/${tenantId}/vertical-packaging/package-entitlements/${entitlementId}/expire`).then((r) => r.data);

export const suspendTenantPackage = (
  tenantId: number | string,
  entitlementId: number | string
): Promise<TenantPackageEntitlement> =>
  api.post(`/api/v1/super/tenants/${tenantId}/vertical-packaging/package-entitlements/${entitlementId}/suspend`).then((r) => r.data);

export const reactivateTenantPackage = (
  tenantId: number | string,
  entitlementId: number | string
): Promise<TenantPackageEntitlement> =>
  api.post(`/api/v1/super/tenants/${tenantId}/vertical-packaging/package-entitlements/${entitlementId}/reactivate`).then((r) => r.data);

export const getTenantVisibilityPreview = (
  tenantId: number | string,
  userId?: number | string,
  scopeKey?: string
): Promise<TenantVisibilityPreview> => {
  const params: Record<string, number | string> = {};
  if (userId !== undefined && userId !== null && String(userId).length > 0) {
    params.userId = userId;
  }
  if (scopeKey) {
    params.scopeKey = scopeKey;
  }
  return api.get(`/api/v1/super/tenants/${tenantId}/vertical-packaging/visibility-preview`, { params }).then((r) => r.data);
};

export const listVerticalDemoTemplates = (): Promise<VerticalDemoTemplate[]> =>
  api.get('/api/v1/admin/vertical-packaging/demo-templates').then((r) => r.data);

export const previewVerticalDemoTemplate = (templateCode: string): Promise<VerticalDemoTemplate> =>
  api.get(`/api/v1/admin/vertical-packaging/demo-templates/${templateCode}/preview`).then((r) => r.data);

export const applyVerticalDemoTemplate = (
  tenantId: number | string,
  templateCode: string,
  payload: ApplyVerticalDemoTemplatePayload
): Promise<VerticalTemplateApplyResult> =>
  api.post(`/api/v1/super/tenants/${tenantId}/vertical-packaging/demo-templates/${templateCode}/apply`, payload).then((r) => r.data);

/* cross-tenant fiscal health (SUPERADMIN-17/18/19) — read-only rollup */
export interface TenantFiscalHealth {
  tenantId: number;
  tenantName: string;
  totalDocuments: number;
  receiptCount: number;
  invoiceCount: number;
  creditNoteCount: number;
  canceledCount: number;
  efacturaLinkedCount: number;
}

export interface FiscalHealthSummary {
  generatedAt: string | null;
  degraded: boolean;
  platformTotals: {
    tenantsWithDocuments: number;
    totalDocuments: number;
    receipts: number;
    invoices: number;
    creditNotes: number;
    canceled: number;
    efacturaLinked: number;
  };
  tenants: TenantFiscalHealth[];
}

export const getFiscalHealth = (): Promise<FiscalHealthSummary> =>
  api.get('/api/v1/super/fiscal-health').then((r) => r.data);

/* offline-queue telemetry (P-OFF-22) — cross-tenant view of client offline-queue depth/failures */
export interface OfflineQueueDevice {
  deviceLabel: string;
  queueDepth: number;
  failedCount: number;
  authBlocked: boolean;
  lastSeenAt: string | null;
  ageSeconds: number;
  stale: boolean;
}

export interface OfflineQueueTenant {
  tenantId: number;
  tenantName: string;
  deviceCount: number;
  queueDepth: number;
  devices: OfflineQueueDevice[];
}

export interface OfflineQueueTelemetry {
  generatedAt: string | null;
  staleAfterSeconds: number;
  totals: {
    tenants: number;
    devices: number;
    queueDepth: number;
    failed: number;
    stale: number;
    authBlocked: number;
  };
  tenants: OfflineQueueTenant[];
}

export const getOfflineQueueTelemetry = (): Promise<OfflineQueueTelemetry> =>
  api.get('/api/v1/super/sync-telemetry').then((r) => r.data);

/* demo tenant matrix */
export const listDemoMatrixProfiles = (): Promise<DemoMatrixProfile[]> =>
  api.get('/api/v1/super/demo-matrix/profiles').then((r) => r.data);

export const previewDemoMatrixProfile = (profileCode: string): Promise<DemoMatrixProfile> =>
  api.get(`/api/v1/super/demo-matrix/profiles/${encodeURIComponent(profileCode)}/preview`).then((r) => r.data);

export const applyDemoMatrixProfile = (
  tenantId: number | string,
  profileCode: string,
  payload: DemoMatrixApplyPayload
): Promise<DemoMatrixApplyResult> =>
  api
    .post(buildDemoMatrixApplyPath(tenantId, profileCode), payload)
    .then((r) => r.data);

/* logs */
export const getLogs = (params: LogQuery = {}): Promise<LogHistoryPage> =>
  api.get(ep.logs, { params }).then(r => normalizeLogHistoryPage(r.data));

/* ---------------- Waitlist (Admin) ---------------- */
export const getWaitlistEntries = (params: {
  tenantId: number | string;
  status?: string;
  partySize?: number;
  startTime?: string; // ISO string
  endTime?: string;   // ISO string
  page?: number;
  size?: number;
  sort?: string;      // e.g., 'createdAt,DESC'
}) => {
  const query: any = { tenantId: params.tenantId };
  if (params.status) query.status = params.status;
  if (params.partySize !== undefined) query.partySize = params.partySize;
  if (params.startTime) query.startTime = params.startTime;
  if (params.endTime) query.endTime = params.endTime;
  if (params.page !== undefined) query.page = params.page;
  if (params.size !== undefined) query.size = params.size;
  if (params.sort) {
    const [field, dir] = params.sort.split(',');
    query.sortBy = field;
    query.sortDirection = dir || 'DESC';
  }
  return api.get('/api/v1/admin/waitlist', { params: query }).then(r => r.data);
};

export const notifyWaitlistEntry = (id: number) =>
  api.post(`/api/v1/admin/waitlist/${id}/notify`).then(r => r.data);

export const updateWaitlistStatus = (id: number, status: string) =>
  api.patch(`/api/v1/admin/waitlist/${id}`, { status }).then(r => r.data);

export const getWaitlistStats = (tenantId: number | string) =>
  api.get(`/api/v1/admin/waitlist/stats/${tenantId}`).then(r => r.data);

/* ---------------- Admin Utils ---------------- */
export const simulateOccupancy = (tenantId: number | string, payload: { freePct: number; heldPct: number; durationMin: number; }) =>
  api.post(`/api/v1/admin/tenants/${tenantId}/simulate-occupancy`, payload).then(r => r.data);

export default {
  ...apiService,
  getUsers,updateUserRoles,deleteUser,
  getReservations,createGuestCustomer,createReservation,confirmReservation,rejectReservation,cancelReservation,
  getTables,createTable,updateTable,deleteTable,
  getSpaces,createSpace,updateSpace,deleteSpace,
  getLocations,createLocation,updateLocation,deleteLocation,
  globalSearch,deleteTenant,getManagers,inviteManager,acceptInvite,getOverview,getLogs,getTenants,
  getTenant, getTenantStats, getTenantWelcome, resendTenantWelcome, createBillingCheckout,
  getWaitlistEntries, notifyWaitlistEntry, updateWaitlistStatus, getWaitlistStats,
  simulateOccupancy,
  getSupportAccessCatalog, getSupportAssignments, getSupportActions, createSupportAction,
  listVerticalPackages, listTenantPackageEntitlements, grantTenantPackage,
  expireTenantPackage, suspendTenantPackage, reactivateTenantPackage,
  getTenantVisibilityPreview, listVerticalDemoTemplates, previewVerticalDemoTemplate,
  applyVerticalDemoTemplate,
  listDemoMatrixProfiles, previewDemoMatrixProfile, applyDemoMatrixProfile,
  getFiscalHealth,
  getOfflineQueueTelemetry,
  setTenantModules
};
